import { type Language } from '@gto/i18n'

export const LANG_ZH_CN = 'zh-cn'
export const LANG_ZH_TW = 'zh-tw'
export const CHINESE_PREFIX = 'zh'
export const TRADITIONAL_CHINESE_SCRIPT = 'hant'
export const TRADITIONAL_CHINESE_REGIONS = ['tw', 'hk', 'mo']

export type RWLanguage = Language | typeof LANG_ZH_CN | typeof LANG_ZH_TW | 'sv' | 'tr'

export const DEFAULT_LANGUAGE: RWLanguage = 'en'

export const SUPPORTED_LANGS: readonly RWLanguage[] = [
  DEFAULT_LANGUAGE,
  'uk',
  LANG_ZH_TW,
  LANG_ZH_CN,
  'pt',
  'es',
  'fr',
  'it',
  'th',
  'ro',
  'pl',
  'hu',
  'ko',
  'de',
  'sl',
  'nl',
  'hr',
  'ru',
  'sv',
  'tr',
]

export const isSupportedLang = (value: unknown): value is RWLanguage =>
  typeof value === 'string' && (SUPPORTED_LANGS as readonly string[]).includes(value)

const parseBrowserLanguage = (): RWLanguage | null => {
  if (typeof navigator === 'undefined') {
    return null
  }

  try {
    const locale = new Intl.Locale(navigator.language)
    const lang = locale.language.toLowerCase()

    if (lang !== CHINESE_PREFIX) {
      return isSupportedLang(lang) ? lang : null
    }

    const script = locale.script?.toLowerCase()
    const region = locale.region?.toLowerCase()

    // Traditional Chinese (Taiwan, Hong Kong, Macau)
    if (
      script === TRADITIONAL_CHINESE_SCRIPT ||
      (region !== undefined && TRADITIONAL_CHINESE_REGIONS.includes(region))
    ) {
      return LANG_ZH_TW
    }

    // All other variants (Simplified, China, Singapore and default)
    return LANG_ZH_CN
  } catch (error) {
    // Fallback if navigator.language contains invalid characters
    // that cause new Intl.Locale() to throw an error
    console.warn('Failed to parse browser language:', error)

    return null
  }
}

export const getInitialLanguage = (userPreferredLang?: string | null): RWLanguage => {
  const storedLang = typeof window === 'undefined' ? null : localStorage.getItem('lang')
  const userDeviceLang = parseBrowserLanguage()

  const candidates = [userPreferredLang, storedLang, userDeviceLang, DEFAULT_LANGUAGE]

  const found = candidates.find(isSupportedLang)

  return found ?? DEFAULT_LANGUAGE
}
